import { apiService } from "./api.service";

class NotificationService {
  // ============ PRIVATE HELPER METHODS ============

  /**
   * Get authentication token from cookie
   * @returns {string|null} Token if found, null otherwise
   */
  getAuthTokenFromCookie() {
    try {
      const cookies = document.cookie.split(";");
      for (let cookie of cookies) {
        const [name, value] = cookie.trim().split("=");
        if (name === "admin_access_token") {
          return value;
        }
      }
    } catch (error) {
      console.warn("Error reading auth token from cookie:", error);
    }
    return null;
  }

  /**
   * Check if user is authenticated
   * @returns {boolean} True if authenticated
   */
  isAuthenticated() {
    const token = this.getAuthTokenFromCookie();
    const userStr = localStorage.getItem("admin_user");
    return !!(token && userStr);
  }

  /**
   * Ensure authentication before making request
   * @throws {Error} If not authenticated
   */
  ensureAuthenticated() {
    if (!this.isAuthenticated()) {
      throw new Error("Not authenticated. Please login first.");
    }
  }

  /**
   * Get request config with proper authentication
   * @param {Object} customConfig - Custom configuration
   * @returns {Object} Request configuration
   */
  getRequestConfig(customConfig = {}) {
    const token = this.getAuthTokenFromCookie();
    const config = {
      withCredentials: true,
      ...customConfig,
    };

    // Add Authorization header if token exists
    if (token) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${token}`,
      };
    }

    return config;
  }

  /**
   * Handle API errors and retry logic
   * @param {Function} requestFn - The API request function
   * @param {number} retries - Number of retry attempts
   * @returns {Promise<any>} API response
   */
  async withRetry(requestFn, retries = 3) {
    for (let i = 0; i < retries; i++) {
      try {
        return await requestFn();
      } catch (error) {
        // If it's the last retry or not a retryable error, throw
        if (
          i === retries - 1 ||
          error?.response?.status === 401 ||
          error?.response?.status === 403
        ) {
          throw error;
        }

        // Wait before retrying (exponential backoff)
        await new Promise((resolve) =>
          setTimeout(resolve, Math.pow(2, i) * 1000)
        );
      }
    }
  }

  // ============ PUBLIC ENDPOINTS ============

  async getActiveNotifications(params = {}) {
    try {
      return await apiService.get("/notifications/notifications/active", {
        params,
        withCredentials: true,
      });
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch active notifications",
        message: error.message,
      };
    }
  }

  // ============ ADMIN ENDPOINTS ============

  async getNotifications(params = {}) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig({ params });
      return await this.withRetry(() =>
        apiService.get("/notifications/notifications", config)
      );
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch notifications",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  async getNotification(id) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      return await this.withRetry(() =>
        apiService.get(`/notifications/notifications/${id}`, config)
      );
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch notification",
        message: error.message,
      };
    }
  }

  async createNotification(data) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      return await this.withRetry(() =>
        apiService.post("/notifications/notifications", data, config)
      );
    } catch (error) {
      // Handle specific error cases
      if (error.response?.status === 401) {
        return {
          success: false,
          status: 401,
          error: "Unauthorized",
          message: "Session expired. Please login again.",
        };
      }

      if (error.response?.status === 403) {
        return {
          success: false,
          status: 403,
          error: "Forbidden",
          message: "You don't have permission to create notifications.",
        };
      }

      return {
        success: false,
        error: "Failed to create notification",
        message: error.message || "Unknown error occurred",
      };
    }
  }

  async updateNotification(id, data) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      return await this.withRetry(() =>
        apiService.put(`/notifications/notifications/${id}`, data, config)
      );
    } catch (error) {
      return {
        success: false,
        error: "Failed to update notification",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  async deleteNotification(id) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      return await this.withRetry(() =>
        apiService.delete(`/notifications/notifications/${id}`, config)
      );
    } catch (error) {
      return {
        success: false,
        error: "Failed to delete notification",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  // ============ STATUS & DISPLAY MANAGEMENT ============

  async toggleNotificationActive(id) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      return await this.withRetry(() =>
        apiService.put(
          `/notifications/notifications/${id}/toggle-active`,
          {},
          config
        )
      );
    } catch (error) {
      return {
        success: false,
        error: "Failed to toggle notification status",
        message: error.message,
      };
    }
  }

  async updateDisplayOrder(id, displayOrder) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      return await this.withRetry(() =>
        apiService.put(
          `/notifications/notifications/${id}/display-order`,
          { display_order: displayOrder },
          config
        )
      );
    } catch (error) {
      return {
        success: false,
        error: "Failed to update display order",
        message: error.message,
      };
    }
  }

  async bulkUpdateDisplayOrders(orderData) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      return await this.withRetry(() =>
        apiService.put(
          "/notifications/notifications/bulk/display-order",
          orderData,
          config
        )
      );
    } catch (error) {
      return {
        success: false,
        error: "Failed to bulk update display orders",
        message: error.message,
      };
    }
  }

  // ============ SPECIAL OPERATIONS ============

  async duplicateNotification(id) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      return await this.withRetry(() =>
        apiService.post(
          `/notifications/notifications/${id}/duplicate`,
          {},
          config
        )
      );
    } catch (error) {
      return {
        success: false,
        error: "Failed to duplicate notification",
        message: error.message,
      };
    }
  }

  async renewNotification(id, expiryDate) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      return await this.withRetry(() =>
        apiService.put(
          `/notifications/notifications/${id}/renew`,
          { expires_at: expiryDate },
          config
        )
      );
    } catch (error) {
      return {
        success: false,
        error: "Failed to renew notification",
        message: error.message,
      };
    }
  }

  async createUrgentNotification(data) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      return await this.withRetry(() =>
        apiService.post("/notifications/notifications/urgent", data, config)
      );
    } catch (error) {
      return {
        success: false,
        error: "Failed to create urgent notification",
        message: error.message,
      };
    }
  }

  async createPromotionalNotification(data) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      return await this.withRetry(() =>
        apiService.post(
          "/notifications/notifications/promotional",
          data,
          config
        )
      );
    } catch (error) {
      return {
        success: false,
        error: "Failed to create promotional notification",
        message: error.message,
      };
    }
  }

  // ============ COLLECTION OPERATIONS ============

  async getNotificationsByType(type, params = {}) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig({ params });
      return await this.withRetry(() =>
        apiService.get(`/notifications/notifications/type/${type}`, config)
      );
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch notifications by type",
        message: error.message,
      };
    }
  }

  async getNotificationsByStyle(style, params = {}) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig({ params });
      return await this.withRetry(() =>
        apiService.get(`/notifications/notifications/style/${style}`, config)
      );
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch notifications by style",
        message: error.message,
      };
    }
  }

  // ============ MAINTENANCE OPERATIONS ============

  async cleanupExpiredNotifications() {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      return await this.withRetry(() =>
        apiService.post(
          "/notifications/notifications/cleanup/expired",
          {},
          config
        )
      );
    } catch (error) {
      return {
        success: false,
        error: "Failed to cleanup expired notifications",
        message: error.message,
      };
    }
  }

  async deactivateExpiredNotifications() {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      return await this.withRetry(() =>
        apiService.post(
          "/notifications/notifications/deactivate/expired",
          {},
          config
        )
      );
    } catch (error) {
      return {
        success: false,
        error: "Failed to deactivate expired notifications",
        message: error.message,
      };
    }
  }

  // ============ STATISTICS ============

  async getNotificationStats() {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      return await this.withRetry(() =>
        apiService.get("/notifications/notifications/stats", config)
      );
    } catch (error) {
      return {
        success: false,
        error: "Failed to fetch notification statistics",
        message: error.message,
      };
    }
  }

  // ============ UTILITY METHODS ============

  /**
   * Validate notification data before sending to API
   * @param {Object} data - Notification data
   * @returns {Object} Validation result
   */
  validateNotificationData(data) {
    const errors = [];

    if (!data.title || data.title.trim().length < 3) {
      errors.push("Title must be at least 3 characters");
    }

    if (!data.message || data.message.trim().length < 5) {
      errors.push("Message must be at least 5 characters");
    }

    if (!data.button_text || data.button_text.trim().length < 2) {
      errors.push("Button text must be at least 2 characters");
    }

    if (!data.type) {
      errors.push("Notification type is required");
    }

    // Validate URL if provided
    if (data.button_url) {
      try {
        new URL(data.button_url);
      } catch {
        errors.push("Invalid button URL format");
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Format notification data for API
   * @param {Object} uiData - UI notification data
   * @returns {Object} API-ready notification data
   */
  formatForAPI(uiData) {
    return {
      title: uiData.title?.trim() || "",
      message: uiData.description?.trim() || uiData.message?.trim() || "",
      button_text: uiData.target?.trim() || uiData.button_text?.trim() || "",
      button_url: uiData.actionUrl?.trim() || uiData.button_url?.trim() || "",
      type: uiData.type || "system_alert",
      style: uiData.style || "info",
      is_active: uiData.status === "active" || uiData.is_active === true,
      display_order: uiData.display_order || 0,
      expires_at: uiData.expires_at || null,
      metadata: uiData.metadata || {},
    };
  }

  /**
   * Format API response for UI display
   * @param {Object} apiData - API notification data
   * @returns {Object} UI-ready notification data
   */
  formatForUI(apiData) {
    return {
      id: apiData.id,
      title: apiData.title,
      description: apiData.message,
      type: apiData.type,
      style: apiData.style || "info",
      status: apiData.is_active ? "active" : "inactive",
      target: apiData.button_text,
      actionUrl: apiData.button_url,
      display_order: apiData.display_order || 0,
      expires_at: apiData.expires_at,
      created_at: apiData.created_at,
      updated_at: apiData.updated_at,
      metadata: apiData.metadata || {},
      is_active: apiData.is_active,
    };
  }
}

export const notificationService = new NotificationService();
